import { PageMeta } from "@/components/PageMeta";
import { SectionHeading } from "@/components/SectionHeading";
import { GlassPanel } from "@/components/GlassPanel";
import { StatusBadge } from "@/components/StatusBadge";
import { CtaLink } from "@/components/Button";
import { Keycap } from "@/components/keycap/Keycap";
import { routeSeo } from "@/content/seo";

const anatomy = [
  { label: "Type", body: "What kind of part this is: code, service, data store or interface." },
  { label: "Configuration", body: "Typed settings a builder sets when placing the component." },
  { label: "Inputs and outputs", body: "The typed contract a component accepts and produces." },
  { label: "Version", body: "A specific, addressable build, not a moving target." },
  { label: "State", body: "Whether the component is untested, tested, or qualified for reuse." },
  {
    label: "Connections",
    body: "The real relationships this instance has to other tiles on a canvas.",
  },
];

export function ComponentsPage() {
  const seo = routeSeo["/components"];
  return (
    <>
      <PageMeta title={seo.title} description={seo.description} path="/components" />

      <section className="container page-hero">
        <p className="eyebrow">Components</p>
        <h1 className="page-hero-title">Typed, versioned building blocks, not scripts</h1>
        <p className="page-hero-lede">
          A Devory component is a manifest-defined part with a type, configuration, real inputs and
          outputs, and a version. Components compose into larger components, and larger components
          into systems.
        </p>
      </section>

      <section className="container section-tight page-diagram">
        <Keycap id="compose" size="xl" state="active" />
      </section>

      <section className="container section" aria-labelledby="anatomy-heading">
        <SectionHeading
          level={2}
          title={<span id="anatomy-heading">What every component carries</span>}
        />
        <div className="grid-3">
          {anatomy.map((item) => (
            <GlassPanel as="article" key={item.label} className="audience-card">
              <h3>{item.label}</h3>
              <p>{item.body}</p>
            </GlassPanel>
          ))}
        </div>
      </section>

      <section className="container section-tight" aria-labelledby="components-status-heading">
        <GlassPanel className="problem-card">
          <div className="capability-card-head">
            <SectionHeading
              level={2}
              title={<span id="components-status-heading">Foundation library, honestly</span>}
            />
            <StatusBadge status="in-development" />
          </div>
          <p>
            Devory defines a foundation library of common component types as structured metadata:
            the shape every component of that kind should have. This is a schema, not a catalogue.
            No ready-to-use, shippable component currently exists in it, and a usable component
            catalogue is still being built out.
          </p>
        </GlassPanel>
      </section>

      <section className="container section-tight">
        <GlassPanel className="cta-banner">
          <h2>See how a component earns trust</h2>
          <p>Before a component is reused, it is tested, checked and versioned. Here is how.</p>
          <div className="cta-banner-actions">
            <CtaLink to="/qualification" variant="secondary">
              Read about qualification
            </CtaLink>
            <CtaLink to="/contact" variant="primary">
              Contact Devory
            </CtaLink>
          </div>
        </GlassPanel>
      </section>
    </>
  );
}
